import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LinearProgress from '@mui/material/LinearProgress';
import { supabase } from '../utils/supabaseClient';
import { useAuth } from '../context/AuthContext';
import * as XLSX from 'xlsx';
import { calculateLocationCost, validateProviderRates, extractServiceType, extractTimestamps, calculateCocaColaRevenue } from '../utils/calculationUtils';
import { calculateBillableHours, getServiceType, calculateHoursBreakdown } from '../utils/serviceTypeUtils';
import { fetchNonPaymentOrderMap, getNonPaymentOrderDetails } from '../utils/nonPaymentUtils';
import CalculationLogger from '../components/calculations/CalculationLogger';

// Helper function for consistent date handling
const getAdjustedDate = (dateInput) => {
  // Handle if input is already a Date object
  if (dateInput instanceof Date) {
    const date = new Date(dateInput);
    date.setUTCHours(12, 0, 0, 0);
    return date;
  }
  
  // Handle string input
  if (typeof dateInput === 'string') {
    // If it's already an ISO string with time, use it as is
    if (dateInput.includes('T')) {
      return new Date(dateInput);
    }
    // Otherwise append noon UTC time to ensure date consistency
    return new Date(`${dateInput}T12:00:00Z`);
  }
  
  // Return null for undefined or null input
  return null;
};

function Calculate() {
  const { uploadId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [calculationStep, setCalculationStep] = useState('preparing');
  const [uploadData, setUploadData] = useState(null);
  const [providers, setProviders] = useState([]);
  const [calculationError, setCalculationError] = useState('');
  const [calculationProgress, setCalculationProgress] = useState(0);
  const [calculationResults, setCalculationResults] = useState(null);
  
  // Enhanced logging state
  const [logMessages, setLogMessages] = useState({
    downloading: [],
    parsing: [],
    processing: [],
    calculating: [],
    saving: []
  });
  const [providerLogs, setProviderLogs] = useState({});
  const [liveStats, setLiveStats] = useState({
    totalAmount: 0,
    providers: 0,
    locations: 0,
    units: 0
  });
  const [phaseStatuses, setPhaseStatuses] = useState({
    downloading: 'pending',
    parsing: 'pending',
    processing: 'pending',
    calculating: 'pending',
    saving: 'pending'
  });
  const [phaseTimestamps, setPhaseTimestamps] = useState({});
  
  const DEBUG_HOURLY = false;
  
  useEffect(() => {
    fetchUploadData();
    fetchProviders();
  }, [uploadId]);

  // Helper methods for structured logging
  const addLogMessage = (message, phase = 'processing', type = 'info') => {
    const logEntry = {
      message,
      type,
      timestamp: Date.now()
    };
    
    setLogMessages(prev => ({
      ...prev,
      [phase]: [...(prev[phase] || []), logEntry].slice(-100) // Keep last 100 messages per phase
    }));
  };

  const updateStats = (updates) => {
    setLiveStats(prev => ({
      ...prev,
      ...updates
    }));
  };

  const addProviderLog = (providerId, providerName, message, type = 'info') => {
    setProviderLogs(prev => ({
      ...prev,
      [providerId]: {
        name: providerName,
        logs: [
          ...((prev[providerId]?.logs || [])),
          { message, type, timestamp: Date.now() }
        ].slice(-50), // Keep last 50 logs per provider
        status: prev[providerId]?.status || 'processing',
        amount: prev[providerId]?.amount || 0,
        locations: prev[providerId]?.locations || 0
      }
    }));
  };

  const updateProviderStatus = (providerId, updates) => {
    setProviderLogs(prev => ({
      ...prev,
      [providerId]: {
        ...prev[providerId],
        ...updates
      }
    }));
  };

  const setPhaseStatus = (phase, status) => {
    setPhaseStatuses(prev => ({
      ...prev,
      [phase]: status
    }));
    
    if (status === 'in-progress') {
      setPhaseTimestamps(prev => ({
        ...prev,
        [`${phase}_start`]: Date.now()
      }));
    } else if (status === 'completed' || status === 'error') {
      setPhaseTimestamps(prev => ({
        ...prev,
        [`${phase}_end`]: Date.now()
      }));
    }
  };

  async function fetchUploadData() {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('uploads')
        .select('*')
        .eq('id', uploadId)
        .single();
        
      if (error) {
        throw error;
      }
      
      setUploadData(data);
    } catch (error) {
      console.error('Error fetching upload data:', error);
      setCalculationError(`Error loading upload data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }

  async function fetchProviders() {
    try {
      const { data, error } = await supabase
        .from('providers')
        .select(`
          *,
          provider_service_rates (
            id,
            service_type_id,
            rate_type,
            flat_rate,
            refusal_rate,
            hourly_rate,
            per_board,
            per_location,
            rate_1_unit,
            rate_2_units,
            rate_3_units,
            rate_4_units,
            rate_5_units,
            rate_6_units,
            rate_7_units,
            rate_8_units,
            rate_9_units,
            rate_10_units,
            service_types (
              id,
              name,
              code,
              rate_type
            )
          )
        `)
        .eq('status', 'active');
        
      if (error) {
        throw error;
      }
      
      setProviders(data || []);
    } catch (error) {
      console.error('Error fetching providers:', error);
      setCalculationError(`Error loading providers: ${error.message}`);
    }
  }

  const handleBack = () => {
    navigate('/upload-data');
  };

  const startCalculation = async () => {
    if (!uploadData || providers.length === 0) {
      setCalculationError('Missing required data. Please try again.');
      return;
    }
    
    try {
      setProcessing(true);
      setCalculationError('');
      setCalculationStep('downloading');
      setCalculationProgress(10);
      setPhaseStatus('downloading', 'in-progress');
      
      // 1. Download the file from storage
      addLogMessage('Downloading file from storage...', 'downloading', 'info');
      addLogMessage(`File: ${uploadData.filename}`, 'downloading', 'info');
      const { data: fileData, error: downloadError } = await supabase.storage
        .from('documents')
        .download(uploadData.file_path);
        
      if (downloadError) {
        setPhaseStatus('downloading', 'error');
        throw new Error(`Error downloading file: ${downloadError.message}`);
      }

      addLogMessage('File downloaded successfully', 'downloading', 'success');
      setPhaseStatus('downloading', 'completed');
      setCalculationStep('parsing');
      setCalculationProgress(30);
      setPhaseStatus('parsing', 'in-progress');
      
      // 2. Parse the file
      addLogMessage('Parsing spreadsheet data...', 'parsing', 'info');
      let workbookData = [];
      
      // Read the file based on type
      if (uploadData.filename.endsWith('.csv')) {
        const text = await fileData.text();
        const workbook = XLSX.read(text, { type: 'string' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        workbookData = XLSX.utils.sheet_to_json(firstSheet);
        addLogMessage('CSV file parsed successfully', 'parsing', 'info');
      } else {
        // For Excel files
        const arrayBuffer = await fileData.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        workbookData = XLSX.utils.sheet_to_json(firstSheet);
        addLogMessage('Excel file parsed successfully', 'parsing', 'info');
      }
      
      if (!workbookData || workbookData.length === 0) {
        setPhaseStatus('parsing', 'error');
        throw new Error('No data found in the uploaded file');
      }
      
      addLogMessage(`Loaded ${workbookData.length} rows from spreadsheet`, 'parsing', 'success');
      setPhaseStatus('parsing', 'completed');
      
      setCalculationStep('processing');
      setCalculationProgress(50);
      setPhaseStatus('processing', 'in-progress');
      
      // 3. Process the data
      addLogMessage('Processing data and calculating payments...', 'processing', 'info');
      
      // Check if required columns exist
      const sampleRow = workbookData[0];
      const requiredColumns = [
        'Address', 'Assigned Service Provider', 'Assigned Resource Name', 'LogistixAI Order Number'
      ];
      
      // Normalize column headers for case-insensitive check
      const normalizedHeaders = Object.keys(sampleRow).map(header => header.toLowerCase());
      const missingColumns = requiredColumns.filter(col => {
        const key = col.toLowerCase();
        // Special case for LogistixAI Order Number
        if (key === 'logistixai order number') {
          return !normalizedHeaders.includes(key) && 
                 !normalizedHeaders.find(h => 
                   h.includes('logistixai order') || 
                   h.includes('logistix order') ||
                   h.includes('logistixai #') ||
                   h.includes('order #')
                 );
        }
        return !normalizedHeaders.includes(key) && 
               !normalizedHeaders.find(h => h.includes(key));
      });
      
      if (missingColumns.length > 0) {
        setPhaseStatus('processing', 'error');
        addLogMessage(`Missing required columns: ${missingColumns.join(', ')}`, 'processing', 'error');
        throw new Error(`Missing required columns: ${missingColumns.join(', ')}`);
      }
      
      addLogMessage('All required columns found', 'processing', 'success');
      
      // Fetch non-payment orders list for filtering (with details including reason)
      addLogMessage('Loading non-payment orders list...', 'processing', 'info');
      const nonPaymentMap = await fetchNonPaymentOrderMap();
      const nonPaymentCount = nonPaymentMap.size;
      if (nonPaymentCount > 0) {
        addLogMessage(`Found ${nonPaymentCount} order(s) in non-payment list`, 'processing', 'info');
      } else {
        addLogMessage('No orders in non-payment list', 'processing', 'info');
      }
      
      // Find the actual column names regardless of case
      const getColumnName = (columnKey) => {
        const key = columnKey.toLowerCase();
        
        // Special case for LogistixAI Order Number
        if (key === 'logistixai order number') {
          return Object.keys(sampleRow).find(header => 
            header.toLowerCase().includes('logistixai order') || 
            header.toLowerCase().includes('logistix order') ||
            header.toLowerCase().includes('logistixai #') ||
            header.toLowerCase().includes('order #')
          );
        }
        
        return Object.keys(sampleRow).find(header => 
          header.toLowerCase() === key || 
          header.toLowerCase().includes(key)
        );
      };
      
      const addressCol = getColumnName('address');
      const serviceProviderCol = getColumnName('assigned service provider');
      const resourceNameCol = getColumnName('assigned resource name');
      const orderNumberCol = getColumnName('logistixai order number');
      const customerNameCol = getColumnName('customer');
      const stateCol = getColumnName('customer state');
      
      // Group by location and service provider
      const locationGroups = {};
      addLogMessage(`Grouping ${workbookData.length} rows by location and provider...`, 'processing', 'info');
      
      workbookData.forEach(row => {
        const location = row[addressCol]?.toString().trim() || 'Unknown Location';
        const serviceProvider = row[serviceProviderCol]?.toString().trim() || '';
        const resourceName = row[resourceNameCol]?.toString().trim() || '';
        const orderNumber = row[orderNumberCol]?.toString().trim() || '';
        const customerName = row[customerNameCol]?.toString().trim() || '';
        const state = row[stateCol]?.toString().trim() || '';
        
        // Extract service type from the row
        const serviceTypeName = extractServiceType(row, sampleRow);
        
        // Extract timestamps for hourly work
        const timestamps = extractTimestamps(row, sampleRow);
        if (DEBUG_HOURLY) {
          console.log('[DBG] Extracted timestamps', { orderNumber, timestamps, row });
        }
        
        if (!orderNumber) return; // Skip rows without order numbers
        
        // Create a provider key that includes service type
        const providerKey = `${location}_${serviceProvider}_${resourceName}_${serviceTypeName || 'Initial Service Call'}`;
        
        if (!locationGroups[providerKey]) {
          locationGroups[providerKey] = {
            location,
            serviceProvider,
            resourceName,
            orderNumbers: new Set(),
            excludedOrders: [], // Track excluded orders with full details for display
            customerName,
            state,
            serviceTypeName: serviceTypeName || 'Initial Service Call',
            timestamps: timestamps,
            rows: []
          };
        }
        
        // Check if this order is in the non-payment list
        const nonPaymentDetails = getNonPaymentOrderDetails(orderNumber, nonPaymentMap);
        if (nonPaymentDetails) {
          // Track excluded order with full details (for display with hours but $0 amount)
          locationGroups[providerKey].excludedOrders.push({
            orderNumber: orderNumber,
            reason: nonPaymentDetails.reason,
            notes: nonPaymentDetails.notes,
            row: row, // Keep the row data for hours extraction
            timestamps: timestamps
          });
          addLogMessage(`Excluded order ${orderNumber} from payment (Reason: ${nonPaymentDetails.reason})`, 'processing', 'warning');
          // Skip this order from paid calculations - don't add to orderNumbers or rows
          return;
        }
        
        // Each LogistixAI order number should be treated as a complete 7-character code
        locationGroups[providerKey].orderNumbers.add(orderNumber);
        locationGroups[providerKey].rows.push(row);
      });
      
      const locationGroupCount = Object.keys(locationGroups).length;
      addLogMessage(`Grouped into ${locationGroupCount} location groups`, 'processing', 'success');
      setPhaseStatus('processing', 'completed');
      
      setCalculationStep('calculating');
      setCalculationProgress(70);
      setPhaseStatus('calculating', 'in-progress');
      
      // 4. Apply rates and calculate amounts
      addLogMessage('Applying provider rates to calculations...', 'calculating', 'info');
      
      const results = {
        providerResults: {},
        totalAmount: 0,
        totalLocations: 0,
        totalUnits: 0
      };
      
      // Track unmatched providers for better error reporting
      const unmatchedProviders = {};
      
      // Normalize string for comparison - handles hidden characters, multiple spaces, etc.
      const normalizeString = (str) => {
        if (!str) return '';
        return str
          .toString()
          // Replace non-breaking spaces (char 160) with regular spaces
          .replace(/\u00A0/g, ' ')
          // Replace other common invisible/special characters
          .replace(/[\u200B-\u200D\uFEFF\u2060]/g, '') // Zero-width chars
          .replace(/[\u2000-\u200A]/g, ' ') // Various unicode spaces
          // Collapse multiple spaces into single space
          .replace(/\s+/g, ' ')
          // Trim and lowercase
          .trim()
          .toLowerCase();
      };
      
      // Helper function to find similar provider names (fuzzy match for suggestions)
      const findSimilarProviders = (searchName) => {
        if (!searchName) return [];
        const searchNorm = normalizeString(searchName);
        return providers
          .filter(p => {
            const providerNorm = normalizeString(p.name);
            // Check if names share significant parts
            const searchParts = searchNorm.split(/\s+/);
            const providerParts = providerNorm.split(/\s+/);
            // Match if any word matches or if there's a partial match
            return searchParts.some(sp => 
              providerParts.some(pp => pp.includes(sp) || sp.includes(pp))
            ) || providerNorm.includes(searchNorm) || searchNorm.includes(providerNorm);
          })
          .map(p => p.name)
          .slice(0, 3); // Return top 3 suggestions
      };
      
      // Function to find the provider in our rate table
      const findProvider = (serviceProvider, resourceName) => {
        const serviceProviderNorm = normalizeString(serviceProvider);
        const resourceNameNorm = normalizeString(resourceName);
        
        // Try exact normalized match
        const provider = providers.find(p => {
          const providerNorm = normalizeString(p.name);
          return providerNorm === serviceProviderNorm || providerNorm === resourceNameNorm;
        });
        
        return provider;
      };
      
      // Process each location group
      let processedCount = 0;
      const totalGroups = Object.keys(locationGroups).length;
      
      for (const key in locationGroups) {
        const group = locationGroups[key];
        const { serviceProvider, resourceName, location, orderNumbers, excludedOrders, customerName, state, serviceTypeName, timestamps } = group;
        
        // Find the provider in our database
        const provider = findProvider(serviceProvider, resourceName);
        
        if (!provider) {
          // Track unmatched provider with details
          // Use normalized name as key to consolidate entries, preferring non-empty values
          const providerName = normalizeString(resourceName) || normalizeString(serviceProvider);
          const searchKey = providerName || '(unknown)';
          
          if (!unmatchedProviders[searchKey]) {
            // Find similar providers for suggestions
            const similarToService = serviceProvider ? findSimilarProviders(serviceProvider) : [];
            const similarToResource = resourceName ? findSimilarProviders(resourceName) : [];
            const allSuggestions = [...new Set([...similarToService, ...similarToResource])];
            
            unmatchedProviders[searchKey] = {
              displayName: resourceName || serviceProvider || '(unknown)',
              foundInServiceProvider: false,
              foundInResourceName: false,
              locations: [],
              orderCount: 0,
              suggestions: allSuggestions
            };
            
            // Log detailed warning on first occurrence
            const warningMsg = `⚠️ Provider not found: "${resourceName || serviceProvider || '(unknown)'}"`; 
            addLogMessage(warningMsg, 'calculating', 'warning');
            
            // Log suggestions if found
            if (allSuggestions.length > 0) {
              addLogMessage(`   💡 Did you mean: ${allSuggestions.join(', ')}?`, 'calculating', 'info');
            }
          }
          
          // Track which columns contained this provider name
          if (serviceProvider) unmatchedProviders[searchKey].foundInServiceProvider = true;
          if (resourceName) unmatchedProviders[searchKey].foundInResourceName = true;
          
          // Track location and order details for this unmatched provider
          unmatchedProviders[searchKey].locations.push(location);
          unmatchedProviders[searchKey].orderCount += orderNumbers.size;
          
          continue;
        }
        
        // Initialize provider log entry if it doesn't exist
        if (!providerLogs[provider.id]) {
          setProviderLogs(prev => ({
            ...prev,
            [provider.id]: {
              name: provider.name,
              logs: [],
              status: 'processing',
              amount: 0,
              locations: 0
            }
          }));
        }
        
        // Calculate billable hours for hourly service types, capturing travel/onsite breakdown
        const breakdown = calculateHoursBreakdown(timestamps);
        if (DEBUG_HOURLY) {
          console.log('[DBG] Hours breakdown', { orderNumber: Array.from(orderNumbers), breakdown });
        }
        let billableHours = 0;
        if (timestamps.billable_hours) {
          // Use explicit billable hours if provided
          billableHours = timestamps.billable_hours;
        } else if (timestamps.travel_time_start || timestamps.arrival_time || timestamps.onsite_minutes || timestamps.travel_minutes) {
          // Calculate from timestamps and/or onsite minutes
          billableHours = breakdown.totalHours;
        }
        
        // Add to results
        if (!results.providerResults[provider.id]) {
          results.providerResults[provider.id] = {
            providerId: provider.id,
            providerName: provider.name,
            locations: [],
            totalCost: 0,
            totalUnits: 0,
            totalLocations: 0,
            totalBillableHours: 0,
            net_terms: provider.net_terms
          };
        }
        
        const unitCount = orderNumbers.size;
        results.totalUnits += unitCount;
        results.providerResults[provider.id].totalUnits += unitCount;
        results.providerResults[provider.id].totalBillableHours += billableHours;
        
        // Is this a refusal calculation?
        const isRefusal = uploadData.file_path && uploadData.file_path.includes('/refusal/');
        
        // Get the service type to find the provider-specific rate configuration
        const serviceType = await getServiceType(serviceTypeName);
        let providerServiceRate = null;
        
        // Find provider-specific service rate configuration for this service type
        if (provider.provider_service_rates && serviceType) {
          // Try to match by service_type_id first
          providerServiceRate = provider.provider_service_rates.find(
            rate => rate.service_type_id === serviceType.id
          );
          
          // If not found by ID, try matching by service type name as fallback
          // This handles cases where the service type might not be properly loaded from database
          if (!providerServiceRate && serviceTypeName) {
            providerServiceRate = provider.provider_service_rates.find(
              rate => rate.service_types && 
                     rate.service_types.name.toLowerCase() === serviceTypeName.toLowerCase()
            );
            if (providerServiceRate) {
              addLogMessage(`Found provider rate for ${provider.name} - ${serviceTypeName} by name match (rate_type: ${providerServiceRate.rate_type})`);
            }
          }
        }
        
        // Debug: Log provider service rate info for hourly services
        if (billableHours > 0 && providerServiceRate) {
          const msg = `Service: ${serviceTypeName}, Rate Type: ${providerServiceRate.rate_type}, Hourly Rate: $${providerServiceRate.hourly_rate}, Hours: ${billableHours.toFixed(2)}`;
          addLogMessage(msg, 'calculating', 'info');
          addProviderLog(provider.id, provider.name, msg, 'info');
        }
        
        // Calculate location cost with service type support
        let locationCost = await calculateLocationCost(provider, unitCount, isRefusal, serviceTypeName, billableHours, providerServiceRate);
        
        if (locationCost === 0) {
          if (billableHours > 0) {
            const hourlyRate = providerServiceRate?.rate_type === 'HOURLY' ? providerServiceRate.hourly_rate : provider.hourly_rate;
            if (!hourlyRate) {
              const msg = `⚠️ No hourly rate defined (Service Type: ${serviceTypeName})`;
              addLogMessage(msg, 'calculating', 'warning');
              addProviderLog(provider.id, provider.name, msg, 'warning');
            }
          } else if (isRefusal) {
            const refusalRate = providerServiceRate?.refusal_rate || provider.rate_refusal;
            if (!refusalRate) {
              const msg = `⚠️ No refusal rate defined`;
              addLogMessage(msg, 'calculating', 'warning');
              addProviderLog(provider.id, provider.name, msg, 'warning');
            }
          }
        } else {
          // Log details about the calculation for transparency
          if (billableHours > 0) {
            // Hourly billing - check provider service rate first, fall back to legacy rate
            const hourlyRate = providerServiceRate?.rate_type === 'HOURLY' ? providerServiceRate.hourly_rate : provider.hourly_rate;
            if (hourlyRate) {
              const msg = `${billableHours.toFixed(2)} hours at $${hourlyRate}/hour = $${locationCost.toFixed(2)} (${serviceTypeName})`;
              addProviderLog(provider.id, provider.name, msg, 'success');
            }
          } else if (isRefusal) {
            const refusalRate = providerServiceRate?.refusal_rate || provider.rate_refusal;
            const msg = `Refusal: ${unitCount} units at $${refusalRate} each = $${locationCost.toFixed(2)}`;
            addProviderLog(provider.id, provider.name, msg, 'success');
          } else {
            // Regular flat rate calculation - use provider service rate if available
            const perBoard = providerServiceRate?.per_board || provider.per_board;
            const perLocation = providerServiceRate?.per_location || provider.per_location;
            
            if (perBoard !== null && perBoard !== undefined) {
              const msg = `${unitCount} units at $${perBoard} each = $${(locationCost - (perLocation || 0)).toFixed(2)} (${serviceTypeName})`;
              addProviderLog(provider.id, provider.name, msg, 'success');
            } else {
              const unitRate = locationCost - (perLocation || 0);
              const msg = `${unitCount} units at flat rate $${unitRate.toFixed(2)} (${serviceTypeName})`;
              addProviderLog(provider.id, provider.name, msg, 'success');
            }
            
            // Add per location fee if applicable
            if (perLocation > 0) {
              const msg = `Added per-location fee of $${perLocation}`;
              addProviderLog(provider.id, provider.name, msg, 'info');
            }
          }
        }
        
        // Calculate Coca-Cola revenue
        const cocaColaRevenue = await calculateCocaColaRevenue(serviceTypeName, isRefusal, unitCount, billableHours);
        
        // Only create the main location entry if there are paid orders (not all excluded)
        if (orderNumbers.size > 0) {
          const locationData = {
            location: location,
            customerName: customerName,
            state: state,
            unitCount: unitCount,
            costAmount: locationCost,
            orderNumbers: Array.from(orderNumbers),
            isRefusal: isRefusal,
            serviceTypeName: serviceTypeName,
            billableHours: billableHours,
            travelHours: breakdown.travelHours || 0,
            onsiteHours: breakdown.onsiteHours || 0,
            cocaColaRevenue: cocaColaRevenue,
            isExcluded: false
          };
          if (DEBUG_HOURLY) {
            console.log('[DBG] Location calculated', locationData);
          }
          
          results.providerResults[provider.id].locations.push(locationData);
          console.log(`Added location to provider ${provider.name}:`, locationData);
          
          results.totalAmount += locationCost;
          results.providerResults[provider.id].totalCost += locationCost;
          results.providerResults[provider.id].totalLocations++;
          results.totalLocations++;
        }
        
        // Process excluded orders for this location group - show them with hours but $0 amount
        if (excludedOrders && excludedOrders.length > 0) {
          for (const excludedOrder of excludedOrders) {
            // Calculate hours breakdown for the excluded order
            const excludedBreakdown = calculateHoursBreakdown(excludedOrder.timestamps);
            let excludedBillableHours = 0;
            if (excludedOrder.timestamps.billable_hours) {
              excludedBillableHours = excludedOrder.timestamps.billable_hours;
            } else if (excludedOrder.timestamps.travel_time_start || excludedOrder.timestamps.arrival_time || excludedOrder.timestamps.onsite_minutes) {
              excludedBillableHours = excludedBreakdown.totalHours;
            }
            
            const excludedLocationData = {
              location: location,
              customerName: customerName,
              state: state,
              unitCount: 1, // Each excluded order counts as 1 unit
              costAmount: 0, // $0 amount for excluded orders
              orderNumbers: [excludedOrder.orderNumber],
              isRefusal: isRefusal,
              serviceTypeName: serviceTypeName,
              billableHours: excludedBillableHours,
              travelHours: excludedBreakdown.travelHours || 0,
              onsiteHours: excludedBreakdown.onsiteHours || 0,
              cocaColaRevenue: 0,
              isExcluded: true,
              exclusionReason: excludedOrder.reason,
              exclusionNotes: excludedOrder.notes
            };
            
            results.providerResults[provider.id].locations.push(excludedLocationData);
            addLogMessage(`Added excluded order ${excludedOrder.orderNumber} with $0 amount (${excludedOrder.reason})`, 'calculating', 'info');
          }
        }
        
        // Update provider status
        updateProviderStatus(provider.id, {
          amount: results.providerResults[provider.id].totalCost,
          locations: results.providerResults[provider.id].totalLocations
        });
        
        // Update live statistics
        processedCount++;
        updateStats({
          totalAmount: results.totalAmount,
          providers: Object.keys(results.providerResults).length,
          locations: results.totalLocations,
          units: results.totalUnits
        });
        
        // Update progress within calculating phase
        const progressPercent = 70 + Math.round((processedCount / totalGroups) * 15);
        setCalculationProgress(progressPercent);
      }
      
      addLogMessage(`Processed ${totalGroups} location groups`, 'calculating', 'success');
      addLogMessage(`Total: $${results.totalAmount.toFixed(2)} across ${Object.keys(results.providerResults).length} providers`, 'calculating', 'success');
      
      // Log summary of unmatched providers
      const unmatchedKeys = Object.keys(unmatchedProviders);
      if (unmatchedKeys.length > 0) {
        const totalUnmatchedOrders = unmatchedKeys.reduce((sum, key) => sum + unmatchedProviders[key].orderCount, 0);
        const totalUnmatchedLocations = unmatchedKeys.reduce((sum, key) => sum + unmatchedProviders[key].locations.length, 0);
        
        addLogMessage(``, 'calculating', 'warning');
        addLogMessage(`⚠️ UNMATCHED PROVIDERS SUMMARY ⚠️`, 'calculating', 'warning');
        addLogMessage(`Found ${unmatchedKeys.length} provider(s) not in Rate Table`, 'calculating', 'warning');
        addLogMessage(`Skipped ${totalUnmatchedOrders} orders across ${totalUnmatchedLocations} location groups`, 'calculating', 'warning');
        addLogMessage(``, 'calculating', 'warning');
        
        unmatchedKeys.forEach(key => {
          const info = unmatchedProviders[key];
          addLogMessage(`❌ "${info.displayName}"`, 'calculating', 'error');
          
          // Show which columns contained the unmatched name
          const foundIn = [];
          if (info.foundInServiceProvider) foundIn.push('Service Provider');
          if (info.foundInResourceName) foundIn.push('Resource Name');
          addLogMessage(`   Found in column(s): ${foundIn.join(', ') || 'Unknown'}`, 'calculating', 'info');
          addLogMessage(`   Orders skipped: ${info.orderCount}`, 'calculating', 'info');
          
          if (info.suggestions.length > 0) {
            addLogMessage(`   💡 Similar names in Rate Table: ${info.suggestions.join(', ')}`, 'calculating', 'info');
          } else {
            addLogMessage(`   💡 No similar names found - check spelling and special characters`, 'calculating', 'info');
          }
        });
        
        addLogMessage(``, 'calculating', 'warning');
        addLogMessage(`To fix: Add these providers to the Rate Table with EXACT name matching`, 'calculating', 'info');
      }
      
      // Mark all providers as completed
      Object.keys(results.providerResults).forEach(providerId => {
        updateProviderStatus(providerId, { status: 'completed' });
      });
      
      setPhaseStatus('calculating', 'completed');
      
      setCalculationStep('saving');
      setCalculationProgress(90);
      setPhaseStatus('saving', 'in-progress');
      
      // 5. Save results to database
      addLogMessage('Saving calculation results to database...', 'saving', 'info');
      
      const resultEntries = [];
      const locationEntries = [];
      
      // Prepare data for calculation_results and location_results tables
      const providerCount = Object.keys(results.providerResults).length;
      addLogMessage(`Saving ${providerCount} provider results...`, 'saving', 'info');
      console.log('Provider results to save:', providerCount);
      
      let savedProviders = 0;
      for (const providerId in results.providerResults) {
        const providerResult = results.providerResults[providerId];
        console.log(`Processing provider ${providerResult.providerName} with ${providerResult.locations.length} locations`);
        
        // Create calculation result entry
        // Build the insert data object dynamically to handle optional columns
        const insertData = {
            upload_id: uploadId,
            provider_id: providerResult.providerId,
            location_count: providerResult.totalLocations,
            total_units: providerResult.totalUnits,
            total_cost: providerResult.totalCost,
            pay_date: uploadData.work_week_to_date && providerResult.net_terms ? 
              (() => {
                try {
                  // Parse the work week to date
                  const toDateStr = uploadData.work_week_to_date;
                  const netTerms = parseInt(providerResult.net_terms);
                  
                  // First, establish a clean date at midnight UTC
                  const baseDateStr = toDateStr.includes('T') ? toDateStr.split('T')[0] : toDateStr;
                  
                  // Create a new date at midnight local time on the to-date to avoid timezone shifts
                  // This ensures a full day is represented correctly
                  const [year, month, day] = baseDateStr.split('-').map(num => parseInt(num));
                  const baseDate = new Date(Date.UTC(year, month - 1, day, 12, 0, 0, 0));
                  
                  // Add the net terms days to this date
                  const payDate = new Date(baseDate);
                  payDate.setUTCDate(baseDate.getUTCDate() + netTerms + 1);
                  
                  // Format as YYYY-MM-DD
                  const payYear = payDate.getUTCFullYear();
                  const payMonth = String(payDate.getUTCMonth() + 1).padStart(2, '0');
                  const payDay = String(payDate.getUTCDate()).padStart(2, '0');
                  
                  return `${payYear}-${payMonth}-${payDay}`;
                } catch (err) {
                  console.error('Error calculating pay date:', err);
                  return null;
                }
              })() : 
              null
        };
        
        // Don't add billable_hours - it doesn't exist in the database yet
        // Once the service types migration is run, uncomment this:
        // if (providerResult.totalBillableHours) {
        //   insertData.billable_hours = providerResult.totalBillableHours;
        // }
        
        const { data: calcResult, error: calcError } = await supabase
          .from('calculation_results')
          .insert([insertData])
          .select()
          .single();
          
        if (calcError) {
          console.error('Error saving calculation result:', calcError);
          addLogMessage(`❌ Error saving calculation for ${providerResult.providerName}: ${calcError.message}`, 'saving', 'error');
          // Don't continue processing this provider if we can't save the calculation
          continue;
        }
        
        resultEntries.push(calcResult);
        savedProviders++;
        addLogMessage(`Saved ${providerResult.providerName}: ${providerResult.locations.length} locations, $${providerResult.totalCost.toFixed(2)}`, 'saving', 'success');
        console.log(`Saved calculation result ${calcResult.id} for ${providerResult.providerName}`);
        
        // Create location result entries
        for (const location of providerResult.locations) {
          // Build location data dynamically
          const locationData = {
              calculation_id: calcResult.id,
              location_address: location.location,
              units: location.unitCount,
              unit_rate: 0, // We'll use total_cost as the combined cost
              base_rate: 0, // We'll use total_cost as the combined cost
              total_cost: location.costAmount,
              customer_name: location.customerName,
              state: location.state,
              order_numbers: location.orderNumbers.join(' '),
              is_excluded: location.isExcluded || false,
              exclusion_reason: location.exclusionReason || null
          };
          
          console.log('Saving location data:', locationData);
          
          // Persist service type fields for accurate Results/PDF/CSV rendering
          if (location.serviceTypeName) {
            locationData.service_type_name = location.serviceTypeName;
            try {
              const serviceTypeMeta = await getServiceType(location.serviceTypeName);
              if (serviceTypeMeta && serviceTypeMeta.rate_type) {
                locationData.rate_type = serviceTypeMeta.rate_type;
              }
            } catch (e) {
              console.warn('Failed to resolve service type metadata for', location.serviceTypeName, e);
            }
          }
          if (location.billableHours) {
            locationData.billable_hours = location.billableHours;
          }
          // Save detailed hours when available
          if (location.travelHours) {
            locationData.travel_hours = location.travelHours;
          }
          if (location.onsiteHours) {
            locationData.onsite_hours = location.onsiteHours;
          }
          
          if (DEBUG_HOURLY) {
            console.log('[DBG] Inserting location_results', locationData);
          }
          const { data: locData, error: locError } = await supabase
            .from('location_results')
            .insert([locationData])
            .select()
            .single();
            
          if (locError) {
            console.error('Error saving location result:', locError);
            addLogMessage(`❌ Error saving location ${location.location}: ${locError.message}`, 'saving', 'error');
          } else {
            locationEntries.push(locData);
          }
        }
        
        // Update progress for saving
        const saveProgress = 90 + Math.round((savedProviders / providerCount) * 8);
        setCalculationProgress(saveProgress);
      }
      
      // Update upload status
      addLogMessage('Updating upload status...', 'saving', 'info');
      const { error: updateError } = await supabase
        .from('uploads')
        .update({
          status: 'processed',
          provider_count: Object.keys(results.providerResults).length,
          total_amount: results.totalAmount
        })
        .eq('id', uploadId);
        
      if (updateError) {
        console.error('Error updating upload status:', updateError);
        addLogMessage(`⚠️ Error updating upload status: ${updateError.message}`, 'saving', 'warning');
      } else {
        addLogMessage('Upload status updated successfully', 'saving', 'success');
      }
      
      addLogMessage(`Saved ${locationEntries.length} location results`, 'saving', 'success');
      setPhaseStatus('saving', 'completed');
      
      setCalculationResults(results);
      setCalculationProgress(100);
      setCalculationStep('completed');
      
      // Determine if this is a refusal calculation
      const isRefusal = uploadData.file_path && uploadData.file_path.includes('/refusal/');
      
      // Final summary
      const calculationType = isRefusal ? 'refusal' : 'completed work';
      addLogMessage(`✓ Calculation complete for ${calculationType} file`, 'saving', 'success');
      addLogMessage(`Total amount: $${results.totalAmount.toFixed(2)}`, 'saving', 'info');
      addLogMessage(`Total providers: ${Object.keys(results.providerResults).length}`, 'saving', 'info');
      addLogMessage(`Total locations: ${results.totalLocations}`, 'saving', 'info');
      addLogMessage(`Total units: ${results.totalUnits}`, 'saving', 'info');
      
      // Navigate to results page with a query parameter for refusal status
      setTimeout(() => {
        navigate(`/results?refusal=${isRefusal}`);
      }, 2000);
      
    } catch (error) {
      console.error('Error processing calculation:', error);
      setCalculationError(`Error processing calculation: ${error.message}`);
      setCalculationStep('error');
      
      // Mark current phase as error
      const currentPhase = calculationStep;
      if (currentPhase && phaseStatuses[currentPhase]) {
        setPhaseStatus(currentPhase, 'error');
        addLogMessage(`❌ ${error.message}`, currentPhase, 'error');
      }
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button 
            startIcon={<ArrowBackIcon />} 
            onClick={handleBack}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h4">
            Calculate Payments
          </Typography>
        </Box>
        
        {calculationError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {calculationError}
          </Alert>
        )}
        
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            File Ready for Processing
          </Typography>
          
          {uploadData && (
            <Alert severity="info" sx={{ mb: 3 }}>
              Selected file: {uploadData.filename}
            </Alert>
          )}
          
          {!processing && calculationStep !== 'completed' && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Button 
                variant="contained" 
                color="primary" 
                size="large"
                onClick={startCalculation}
                disabled={processing}
              >
                Start Calculation
              </Button>
            </Box>
          )}
          
          {processing && (
            <Box sx={{ mt: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="body1" sx={{ fontWeight: 'medium', mr: 1 }}>
                  {calculationStep === 'downloading' && 'Downloading file...'}
                  {calculationStep === 'parsing' && 'Parsing spreadsheet...'}
                  {calculationStep === 'processing' && 'Processing data...'}
                  {calculationStep === 'calculating' && 'Calculating payments...'}
                  {calculationStep === 'saving' && 'Saving results...'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {calculationProgress}%
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={calculationProgress} 
                sx={{ height: 8, borderRadius: 2 }}
              />
              
              <CalculationLogger
                logMessages={logMessages}
                providerLogs={providerLogs}
                liveStats={liveStats}
                phaseStatuses={phaseStatuses}
                calculationProgress={calculationProgress}
                calculationStep={calculationStep}
              />
            </Box>
          )}
          
          {calculationStep === 'completed' && (
            <Alert severity="success" sx={{ mt: 3 }}>
              Calculation completed successfully! Redirecting to results...
            </Alert>
          )}
        </Box>
      </Paper>
    </Box>
  );
}

export default Calculate; 